import { Injectable, inject } from "@angular/core";

import { Centrifuge, Subscription } from "centrifuge";

import { BetEventType, BetPush } from "@models";
import { Utils } from "@utils";

import { ActionService } from "./action.service";
import { ClientService } from "./client.service";
import { APP_CONFIG } from "./http.service";

@Injectable({ providedIn: "root" })
export class WebSocketService {
  private readonly wsUrl = inject(APP_CONFIG).wsUrl;
  private readonly clS = inject(ClientService);
  private readonly acS = inject(ActionService);

  private centrifuge: Centrifuge | null = null;
  private attempts = 0;

  private readonly subscriptions: Subscription[] = [];
  private readonly MAX = 10;

  private readonly visibleFn = (): void => {
    if (!this.centrifuge) {
      return;
    }

    if (document.hidden) {
      return;
    }

    if (this.centrifuge.state === "disconnected") {
      this.attempts = 0;
      this.centrifuge.connect();
    }
  };

  public init(): void {
    const { token, session_id, game_id } = this.acS.sR$()!;

    this.centrifuge = new Centrifuge(this.wsUrl, { data: { token } });

    this.subscribe<BetPush>(`private:${game_id}#${session_id}`, msg => {
      if (msg.success) {
        this.acS.bE$.next({ type: BetEventType.S, updated_at: msg.success.update_at, free_bet_id: msg.free_bet_id, ...msg.success.state });
      } else if (msg.error) {
        this.acS.bE$.next({ id: msg.id, type: BetEventType.E, error_code: msg.error.error_code });
      } else if (msg.cancel) {
        this.acS.bE$.next({ id: msg.id, type: BetEventType.C, cancel_reason: msg.cancel.cancel_reason });
      }
    });

    this.centrifuge
      .on("connecting", () => {
        if (this.attempts !== 0) {
          this.clS.dis$.set(true);
        }

        this.attempts++;

        if (this.attempts >= this.MAX) {
          this.centrifuge?.disconnect();
        }
      })
      .on("disconnected", () => this.clS.dis$.set(true))
      .on("error", () => this.clS.dis$.set(true))
      .on("connected", () => {
        this.attempts = 0;

        if (this.clS.dis$()) {
          this.clS.dis$.set(false);

          this.clS.ref$.next();

          setTimeout(() => this.clS.dis$.set(null), Utils.second * 1.5);
        }
      });

    this.centrifuge.connect();

    document.addEventListener("visibilitychange", this.visibleFn);
  }

  public destroy(): void {
    document.removeEventListener("visibilitychange", this.visibleFn);

    this.subscriptions.forEach(event => {
      event.unsubscribe();
      event.removeAllListeners();
    });
    if (this.centrifuge) {
      this.centrifuge.disconnect();
      this.centrifuge.removeAllListeners();
    }
  }

  private subscribe<T>(channel: string, callbackFn: (data: T) => void): void {
    const event: Subscription = this.centrifuge!.newSubscription(channel);
    event.on("publication", ctx => ctx.data && !this.clS.stop$() && callbackFn(ctx.data as T)).subscribe();
    this.subscriptions.push(event);
  }
}
